import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Link as LinkIcon, CalendarX } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { addDays, mondayOf, toISO } from "@/lib/dateUtils";
import { AdminAgendaGrid, AdminMobileDay } from "@/components/AgendaGrid";
import AdminNav from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function useIsMobile() {
    const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth < 768);
    useEffect(() => { const on = () => setM(window.innerWidth < 768); window.addEventListener("resize", on); return () => window.removeEventListener("resize", on); }, []);
    return m;
}

export default function AdminAgenda() {
    const isMobile = useIsMobile();
    const [monday, setMonday] = useState(() => mondayOf(new Date()));
    const [week, setWeek] = useState(null);
    const [dayIndex, setDayIndex] = useState(() => Math.min(5, (new Date().getDay() + 6) % 7));
    const [bookingDetail, setBookingDetail] = useState(null);
    const [moveMode, setMoveMode] = useState(false);
    const [showBlockPeriod, setShowBlockPeriod] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const loadWeek = async (mDate) => {
        try { const { data } = await api.get(`/admin/week`, { params: { date: toISO(mDate) } }); setWeek(data); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { loadWeek(monday); }, [monday]);
    const shiftWeek = (n) => setMonday(addDays(monday, n * 7));

    const openBooking = async (bid) => {
        try { const { data } = await api.get(`/admin/bookings/${bid}`); setBookingDetail(data); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleCell = async (date, hour, minute) => {
        if (moveMode) {
            try {
                await api.post(`/admin/bookings/${moveMode}/move`, { new_date: date, new_start_hour: hour, new_start_minute: minute });
                toast.success("Prenotazione spostata");
                setMoveMode(false); setBookingDetail(null); loadWeek(monday);
            } catch (e) { toast.error(formatApiError(e)); }
            return;
        }
        try { await api.post(`/admin/bulk-block`, { action: "toggle", slots: [{ date, hour, minute }] }); loadWeek(monday); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const bulkAction = async (action, slots) => {
        try {
            const { data } = await api.post(`/admin/bulk-block`, { action, slots: slots.map((s) => ({ date: s.date, hour: s.hour, minute: s.minute })) });
            toast.success(`${data.changed} celle modificate${data.skipped_booked ? ` (${data.skipped_booked} prenotate saltate)` : ""}`);
            loadWeek(monday);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const copyLink = async () => {
        const url = `${window.location.origin}/`;
        try { await navigator.clipboard.writeText(url); toast.success("Link copiato"); }
        catch { toast.error("Impossibile copiare"); }
    };

    return (
        <div className="min-h-screen bg-white">
            <AdminNav />
            <div className="max-w-6xl mx-auto px-4 py-5">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2">
                        <button className="p-2" onClick={() => shiftWeek(-1)} data-testid="admin-prev-week"><ChevronLeft size={22} /></button>
                        <div className="text-sm md:text-base font-semibold" data-testid="admin-week-label">{week?.week_label || "…"}</div>
                        <button className="p-2" onClick={() => shiftWeek(1)} data-testid="admin-next-week"><ChevronRight size={22} /></button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-link-btn"><LinkIcon size={14} className="mr-1" />COPIA LINK</Button>
                        <Button variant="outline" size="sm" onClick={() => setShowBlockPeriod(true)} data-testid="block-period-btn"><CalendarX size={14} className="mr-1" />BLOCCA PERIODO</Button>
                    </div>
                </div>

                {moveMode && (
                    <div className="mb-3 border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3 flex items-center justify-between">
                        <span>Modalità spostamento: clicca la nuova ora iniziale disponibile (anche mezz'ora).</span>
                        <Button variant="outline" size="sm" onClick={() => setMoveMode(false)}>Annulla</Button>
                    </div>
                )}

                {week && (isMobile ? (
                    <AdminMobileDay weekData={week} dayIndex={dayIndex} setDayIndex={setDayIndex}
                        onToggleCell={toggleCell} onBookingClick={openBooking} onBulkAction={bulkAction} />
                ) : (
                    <AdminAgendaGrid weekData={week} onToggleCell={toggleCell} onBookingClick={openBooking} onBulkAction={bulkAction} />
                ))}

                <div className="mt-6 flex items-center gap-4 text-xs text-gray-600 flex-wrap">
                    <div className="flex items-center gap-2"><span className="inline-block w-4 h-4 border border-gray-300" style={{ backgroundColor: "#22C55E" }}></span>Disponibile</div>
                    <div className="flex items-center gap-2"><span className="inline-block w-4 h-4 border border-gray-300" style={{ backgroundColor: "#E5E7EB" }}></span>Bloccata da te</div>
                    <div className="flex items-center gap-2"><span className="inline-block w-4 h-4 border border-gray-300" style={{ backgroundColor: "#D1D5DB" }}></span>Prenotata</div>
                </div>
            </div>

            <Dialog open={!!bookingDetail} onOpenChange={(v) => !v && setBookingDetail(null)}>
                <DialogContent className="max-w-md" data-testid="booking-detail-dialog">
                    <DialogHeader>
                        <DialogTitle>Dettaglio prenotazione</DialogTitle>
                        <DialogDescription>Stato: {bookingDetail?.status}</DialogDescription>
                    </DialogHeader>
                    {bookingDetail && (
                        <div className="text-sm space-y-1 border border-gray-200 bg-gray-50 p-3">
                            <div><span className="text-gray-500">Nome:</span> <b>{bookingDetail.first_name} {bookingDetail.last_name}</b></div>
                            <div><span className="text-gray-500">Email:</span> <b>{bookingDetail.email}</b></div>
                            <div><span className="text-gray-500">Telefono:</span> <b>{bookingDetail.phone}</b></div>
                            <div><span className="text-gray-500">Data:</span> <b>{bookingDetail.weekday_name} {bookingDetail.date_label}</b></div>
                            <div><span className="text-gray-500">Orario:</span> <b>{bookingDetail.time_label}</b></div>
                            <div><span className="text-gray-500">Note:</span> {bookingDetail.notes || "(nessuna)"}</div>
                            <div className="text-xs text-gray-400 mt-2">Creata: {new Date(bookingDetail.created_at).toLocaleString("it-IT")}</div>
                        </div>
                    )}
                    <DialogFooter className="flex-col gap-2 sm:flex-row">
                        <Button variant="outline" onClick={() => { setMoveMode(bookingDetail.id); setBookingDetail(null); }}>SPOSTA</Button>
                        <Button variant="destructive" onClick={() => setConfirmDelete(bookingDetail)}>ELIMINA</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confermi l'eliminazione?</AlertDialogTitle>
                        <AlertDialogDescription>La prenotazione verrà annullata e il cliente riceverà un'email.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction className="bg-red-600 hover:bg-red-700"
                            onClick={async () => {
                                try { await api.delete(`/admin/bookings/${confirmDelete.id}`); toast.success("Eliminata"); setConfirmDelete(null); setBookingDetail(null); loadWeek(monday); }
                                catch (e) { toast.error(formatApiError(e)); }
                            }}>Elimina</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <BlockPeriodDialog open={showBlockPeriod} onOpenChange={setShowBlockPeriod} onDone={() => loadWeek(monday)} />
        </div>
    );
}

function BlockPeriodDialog({ open, onOpenChange, onDone }) {
    const [start, setStart] = useState(""); const [end, setEnd] = useState("");
    const [conflicts, setConflicts] = useState(null); const [loading, setLoading] = useState(false);
    useEffect(() => { if (open) { setStart(""); setEnd(""); setConflicts(null); } }, [open]);
    const submit = async () => {
        if (!start || !end) { toast.error("Inserisci entrambe le date"); return; }
        setLoading(true);
        try {
            const { data } = await api.post(`/admin/block-period`, { start_date: start, end_date: end });
            if (data.conflicts && data.conflicts.length > 0) setConflicts(data.conflicts);
            else { toast.success("Periodo bloccato"); onOpenChange(false); onDone && onDone(); }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Blocca periodo</DialogTitle>
                    <DialogDescription>Tutti gli orari nel periodo diventeranno indisponibili. Le prenotazioni esistenti NON vengono cancellate.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div><Label>Data iniziale</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
                    <div><Label>Data finale</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
                    {conflicts && (
                        <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                            <div className="font-bold mb-1">Prenotazioni presenti nel periodo:</div>
                            <ul className="list-disc pl-4">
                                {conflicts.map((c) => (
                                    <li key={c.id}>{c.name} — {c.date} {String(c.start_hour).padStart(2,"0")}:{String(c.start_minute||0).padStart(2,"0")}</li>
                                ))}
                            </ul>
                            <div className="mt-2">Non sono state cancellate. Gestiscile dall'agenda.</div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
                    <Button className="bg-green-700 hover:bg-green-800 text-white" onClick={submit} disabled={loading}>{loading ? "Applico…" : "APPLICA BLOCCO"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
